import { useCallback, useEffect, useRef, useState } from 'react';

type ActionCableSubscription = {
  channel: string;
  params?: Record<string, unknown>;
};

type ActionCableState = 'idle' | 'connecting' | 'connected' | 'disconnected';

type UseActionCableOptions = {
  onMessage?: (data: Record<string, unknown>) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
};

export const useActionCable = (options: UseActionCableOptions = {}) => {
  const [state, setState] = useState<ActionCableState>('idle');
  const wsRef = useRef<WebSocket | null>(null);
  const optionsRef = useRef(options);

  useEffect(() => {
    optionsRef.current = options;
  });

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setState('idle');
  }, []);

  const subscribe = useCallback(
    (subscription: ActionCableSubscription) => {
      disconnect();
      setState('connecting');

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(`${protocol}//${window.location.host}/cable`);
      wsRef.current = ws;

      ws.onopen = () => {
        const identifier = JSON.stringify({
          channel: subscription.channel,
          ...subscription.params,
        });
        ws.send(JSON.stringify({ command: 'subscribe', identifier }));
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'confirm_subscription') {
          setState('connected');
          optionsRef.current.onConnect?.();
          return;
        }

        if (data.type === 'reject_subscription') {
          setState('disconnected');
          optionsRef.current.onDisconnect?.();
          disconnect();
          return;
        }

        if (data.message) {
          optionsRef.current.onMessage?.(data.message);
        }
      };

      ws.onclose = () => {
        setState('disconnected');
        optionsRef.current.onDisconnect?.();
      };
    },
    [disconnect],
  );

  useEffect(() => {
    return () => {
      wsRef.current?.close();
    };
  }, []);

  return { subscribe, disconnect, state };
};
